import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { toast } from "sonner";
import { ShieldCheck, Play, CheckCircle2, XCircle, Loader2, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

export const PredeployCheck = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/predeploy-check/status");
      setData(r.data);
      if (!r.data.running && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const run = async () => {
    setStarting(true);
    try {
      await api.post("/admin/predeploy-check/run");
      toast.success(t("deploycheck.started"));
      await load();
      pollRef.current = setInterval(load, 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "preview_only") toast.error(t("deploycheck.preview_only"));
      else if (detail === "already_running") toast.info(t("deploycheck.running"));
      else toast.error(detail || "Error");
    } finally {
      setStarting(false);
    }
  };

  const last = data?.last_run;
  const running = data?.running || last?.status === "running";
  const verdict = last?.status;

  return (
    <div className="flex flex-col gap-4" data-testid="predeploy-check-panel">
      <div className="sw-card bg-[#f8fafc] border border-[#e2e8f0]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-white border border-[#e2e8f0] flex items-center justify-center text-[#475569] shrink-0">
            <ShieldCheck size={18} />
          </div>
          <div className="text-[13px] text-[#475569] leading-relaxed">
            <strong className="text-[#1e293b]">{t("deploycheck.title")}</strong> — {t("deploycheck.desc")}
            {data && !data.preview && (
              <div className="mt-2 flex items-center gap-2 text-[#b45309]" data-testid="predeploy-prod-warning">
                <AlertTriangle size={14} /> {t("deploycheck.preview_only")}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-[12px] text-[#747474]" data-testid="predeploy-last-run-info">
          {last ? `${t("deploycheck.last_run")}: ${new Date(last.started_at).toLocaleString()} · ${t("deploycheck.by")} ${last.triggered_by}` : t("deploycheck.never_run")}
        </div>
        <button
          data-testid="predeploy-run-btn"
          onClick={run}
          disabled={starting || running || (data && !data.preview)}
          className="sw-btn-primary"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {running ? t("deploycheck.running") : t("deploycheck.run_btn")}
        </button>
      </div>

      {running && (
        <div className="sw-card border border-[#e2e8f0] flex items-center gap-3" data-testid="predeploy-running-card">
          <Loader2 size={20} className="animate-spin text-[#475569]" />
          <div className="text-[13px] text-[#475569]">{t("deploycheck.running_hint")}</div>
        </div>
      )}

      {!running && last && verdict !== "running" && (
        <div
          data-testid="predeploy-verdict-card"
          className={`sw-card border-2 ${verdict === "passed" ? "border-[#16a34a] bg-[#f0fdf4]" : "border-[#dc2626] bg-[#fef2f2]"}`}
        >
          <div className="flex items-center gap-3">
            {verdict === "passed"
              ? <CheckCircle2 size={32} className="text-[#16a34a]" />
              : <XCircle size={32} className="text-[#dc2626]" />}
            <div>
              <div className={`text-[20px] font-bold ${verdict === "passed" ? "text-[#15803d]" : "text-[#b91c1c]"}`} data-testid="predeploy-verdict-text">
                {verdict === "passed" ? t("deploycheck.go") : t("deploycheck.nogo")}
              </div>
              <div className="text-[13px] text-[#475569]">
                {t("deploycheck.passed")}: <strong>{last.passed}</strong> · {t("deploycheck.failed")}: <strong>{last.failed}</strong> · {t("deploycheck.total")}: <strong>{last.total}</strong>
                {last.skipped > 0 && <> · {t("deploycheck.skipped")}: {last.skipped}</>}
              </div>
            </div>
          </div>

          {last.failures?.length > 0 && (
            <div className="mt-4" data-testid="predeploy-failures-list">
              <div className="text-[12px] uppercase tracking-wider text-[#b91c1c] font-semibold mb-2">{t("deploycheck.failures")}</div>
              <div className="flex flex-col gap-2">
                {last.failures.map((f, i) => (
                  <div key={i} className="bg-white border border-[#fecaca] rounded-[10px] px-3 py-2">
                    <div className="text-[12px] font-mono text-[#1e293b]">{f.name}</div>
                    <div className="text-[11px] text-[#991b1b] font-mono break-all">{f.message}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {last.output_tail && (
            <div className="mt-3">
              <button
                data-testid="predeploy-toggle-output"
                onClick={() => setShowOutput((v) => !v)}
                className="text-[12px] text-[#475569] flex items-center gap-1 hover:underline"
              >
                {showOutput ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {t("deploycheck.output")}
              </button>
              {showOutput && (
                <pre className="mt-2 bg-[#0f172a] text-[#e2e8f0] text-[11px] rounded-[10px] p-3 overflow-x-auto max-h-[300px] overflow-y-auto whitespace-pre-wrap">{last.output_tail}</pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PredeployCheck;
